import React, { useContext, useState } from 'react';
import IconButton from '../../../icons/IconButton';
import { IconType } from '../../../icons/Icon';
import { Button, ButtonVariant, DeleteButton, SaveButton } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { DropdownSearchSelect, DropdownSelect } from '../../../components/Dropdown';
import { TextInput } from '../../../components/TextInput';
import { PdfFontFamily, PdfPageFormat, PdfOrientation } from '../PdfComponent';
import { usePdfTemplateContext } from '../PdfTemplateEditorContext';
import { PdfTemplateUtil } from '../pdfTemplate.util';
import { OrderContext } from '../../../../context/OrderContext';
import { Order } from '../../../../types/order';
import { PdfTemplateEntitySubtype, PdfTemplateEntityType, PdfTemplateInput } from '../../../../types/pdf_template';
import { CompanyContext } from '../../../../context/CompanyContext';
import { useRequest } from '../../../../requests/common/requestHook';
import { PdfTemplateRequest } from '../../../../requests/pdf.request';
import { ExportUtil } from '../../../../util/export.util';
import PdfTemplateJsonPreviewModal from './PdfTemplateJsonPreviewModal';
import { UserContext } from '../../../../context/UserContext';
import PdfTemplateConfirmDeleteModal from './PdfTemplateConfirmDeleteModal';

const { PAGE_SIZES } = PdfTemplateUtil;

export default function PdfTemplateEditorSidebar<T>({ onSave }: { onSave: () => void }) {
  const { currentUser } = useContext(UserContext);
  const {
    input,
    documentProperties,
    updateDocumentProperties,
    components,
    setComponents,
    selectedId,
    setSelectedId,
    editorSettings,
    updateEditorSettings,
    selectedEntity,
    setSelectedEntity,
  } = usePdfTemplateContext<T>();
  const { orders } = useContext(OrderContext);
  const { currentCompany } = useContext(CompanyContext);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [jsonPreviewModalOpen, setJsonPreviewModalOpen] = useState(false);

  const [exportSample, { loading: pdfSampleLoading }] = useRequest(PdfTemplateRequest.sample);

  const handleExportSample = async () => {
    if (!selectedEntity) return;
    const response = await exportSample({
      entityId: (selectedEntity as any).id,
      template: new PdfTemplateInput({
        name: 'Sample Template',
        entityType: PdfTemplateEntityType.order,
        entitySubtype: PdfTemplateEntitySubtype.inbound,
        companyId: currentCompany.id,
        properties: {
          document: documentProperties,
          components: components,
        },
      }).forCreate(),
    });
    if (!response) return;

    ExportUtil.openInNewTab(response);
  };

  return (
    <div
      className='relative flex h-full flex-col gap-4 overflow-y-auto border-r border-gray-300 p-4'
      style={{ width: editorSettings.sidebarOpen ? editorSettings.sidebarWidth : 0 }}
    >
      <div className='flex items-center justify-between'>
        <p className='page-title'>Settings</p>
        <IconButton
          type={IconType.x}
          text={'Close sidebar'}
          onClick={() => updateEditorSettings({ sidebarOpen: false })}
        />
      </div>

      {/* Resizer handle */}
      <div
        className='absolute right-0 top-0 h-full w-2 cursor-col-resize'
        onMouseDown={e => {
          const startX = e.clientX;
          const startWidth = editorSettings.sidebarWidth;
          const onMove = (ev: MouseEvent) => {
            const dx = startX - ev.clientX;
            const next = Math.max(330, Math.min(560, startWidth - dx));
            updateEditorSettings({ sidebarWidth: next });
          };
          const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
          };
          window.addEventListener('mousemove', onMove);
          window.addEventListener('mouseup', onUp);
        }}
      />

      <div className='grid gap-4'>
        <DropdownSearchSelect<Order>
          label={'Order previews'}
          placeholder={'Select order...'}
          value={selectedEntity as Order | undefined}
          textBuilder={s => s.number || ''}
          items={[...orders.values()]}
          onSelect={order => setSelectedEntity(order as T)}
        />

        <div className='grid gap-1'>
          <span className='text-sm text-gray-700'>Zoom {Math.round(editorSettings.zoom * 100)}%</span>
          <div className='flex items-center gap-3'>
            <span className='text-xs text-gray-500'>50%</span>
            <input
              type='range'
              min={50}
              max={200}
              step={1}
              value={Math.round(editorSettings.zoom * 100)}
              onChange={e => updateEditorSettings({ zoom: Number(e.currentTarget.value) / 100 })}
              className='w-32 accent-primary'
            />
            <span className='text-xs text-gray-500'>200%</span>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-2'>
          <div className='col-span-2'>
            <Checkbox
              label={'Grid Mode'}
              onChange={value => updateEditorSettings({ gridMode: value })}
              value={editorSettings.gridMode}
            />
          </div>
          <div className='col-span-1'>
            <TextInput
              label={'Grid size (mm)'}
              type='number'
              min={1}
              max={50}
              step={1}
              value={editorSettings.gridMm}
              onChange={e => updateEditorSettings({ gridMm: Math.max(1, Math.round(Number(e))) })}
            />
          </div>
        </div>

        <div className='flex gap-2'>
          <Button
            variant={ButtonVariant.secondary}
            text={'Generate sample'}
            loading={pdfSampleLoading}
            onClick={handleExportSample}
            disabled={!selectedEntity}
          />

          <SaveButton text={'Save'} onClick={() => onSave()} />
        </div>
      </div>

      <div className='mt-2 flex flex-col gap-2'>
        <p className='section-title'>Document defaults</p>

        <div className='grid grid-cols-3 gap-2'>
          <div className='col-span-1'>
            <DropdownSelect
              label={'Format'}
              value={documentProperties.pageFormat}
              textBuilder={s => s}
              items={Object.values(PdfPageFormat)}
              onSelect={pageSize => updateDocumentProperties({ pageFormat: pageSize })}
            />
          </div>
          <div className='col-span-2'>
            <DropdownSelect
              label={'Orientation'}
              value={documentProperties.orientation}
              textBuilder={s => s}
              items={Object.values(PdfOrientation)}
              onSelect={orientation => updateDocumentProperties({ orientation })}
            />
          </div>
        </div>

        <div className='grid grid-cols-2 gap-2'>
          {(() => {
            const { width, height } = PAGE_SIZES[documentProperties.pageFormat][documentProperties.orientation];
            return (
              <>
                <div className='col-span-1'>
                  <TextInput label={'Width (mm)'} value={width} onChange={() => {}} disabled />
                </div>
                <div className='col-span-1'>
                  <TextInput label={'Height (mm)'} value={height} onChange={() => {}} disabled />
                </div>
              </>
            );
          })()}
        </div>

        <div className='grid grid-cols-3 gap-2'>
          <div className='col-span-2'>
            <DropdownSelect
              label={'Font Family'}
              value={documentProperties.font.family}
              textBuilder={f => String(f)}
              items={Object.values(PdfFontFamily)}
              onSelect={fontFamily =>
                updateDocumentProperties({ font: { ...documentProperties.font, family: fontFamily } })
              }
            />
          </div>
          <div className='col-span-1'>
            <TextInput
              type='number'
              label={'Font size'}
              value={documentProperties.font.size}
              onChange={value =>
                updateDocumentProperties({ font: { ...documentProperties.font, size: Number(value) } })
              }
            />
          </div>
        </div>
      </div>

      {/* COMPONENT LIST */}
      <div className=''>
        <h3 className='text-lg font-semibold'>Components</h3>
        <div className='mt-2 space-y-1'>
          {components.map((c, idx) => {
            const isSelected = selectedId === c.id;
            const selectedClass = isSelected ? 'border-blue-400 bg-blue-100' : 'bg-white';
            return (
              <div
                key={c.id}
                draggable
                className={`flex cursor-grab items-center justify-between rounded-2 border px-2 py-1 text-xs ${selectedClass}`}
                onClick={() => setSelectedId(c.id)}
                onDragStart={e => {
                  e.dataTransfer.setData('text/plain', String(idx));
                }}
                onDragOver={e => {
                  e.preventDefault();
                }}
                onDrop={e => {
                  e.preventDefault();
                  const fromIdxStr = e.dataTransfer.getData('text/plain');
                  const fromIdx = parseInt(fromIdxStr, 10);
                  if (isNaN(fromIdx) || fromIdx === idx) return;
                  const arr = [...components];
                  const [moved] = arr.splice(fromIdx, 1);
                  arr.splice(idx, 0, moved);
                  setComponents(arr);
                }}
              >
                <span className='truncate'>
                  {c.type}
                  {c.name ? ` - ${c.name}` : ''}
                </span>
                <span className='text-xs text-gray-500'>#{idx + 1}</span>
              </div>
            );
          })}
        </div>
      </div>

      {input.id ? (
        <div>
          <PdfTemplateConfirmDeleteModal open={deleteModalOpen} setOpen={setDeleteModalOpen} pdfTemplate={input} />
          <DeleteButton text={'Delete template'} onClick={() => setDeleteModalOpen(true)} />
        </div>
      ) : null}

      {/* DEV OPTIONS */}
      {currentUser?.email.includes('@ventory.io') && (
        <>
          <PdfTemplateJsonPreviewModal open={jsonPreviewModalOpen} setOpen={setJsonPreviewModalOpen} />

          <div className='grid gap-2'>
            <p className='section-title'>Developer Options</p>

            <Button
              disabled={pdfSampleLoading}
              text={'Review JSON'}
              onClick={() => setJsonPreviewModalOpen(true)}
              variant={ButtonVariant.secondary}
            />
          </div>
        </>
      )}
    </div>
  );
}
